import { useEffect, useState, useCallback, useRef } from 'react';
import { LineChart, Line, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Wallet, PiggyBank, ArrowUpRight, ArrowDownRight, AlertTriangle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { getCharts } from '../lib/api';
import { formatAmount, getIcon, catColor, catTint } from '../lib/constants';
import { periodRange } from '../components/home/PeriodStepper';
import { dateRangeKey } from '../lib/dateRanges';
import { useAmountsVisibility } from '../context/AmountsVisibilityContext';
import { DateRangePicker } from '../components/DateRangePicker';
import { Skeleton } from '../components/ui/skeleton';
import { ProfileButton } from '../components/Sidebar';
import { ChartTooltip, useChartTheme } from '../theme/chartTheme';

// Percentage change vs the previous period; null when it can't be computed.
function pctChange(cur, prev) {
  if (prev === null || prev === undefined) return null;
  if (prev === 0) return null;
  return ((cur - prev) / Math.abs(prev)) * 100;
}

// Timeline keys from GET /charts are local calendar keys ("2026-10-25",
// "2026-10" for months). Parse them as local dates — new Date("2026-10-25")
// is UTC midnight, which shows as the 24th in El Salvador.
function parseKey(key) {
  const [y, m, d] = String(key).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
const shortDay = (d) => new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short' }).format(d).replace('.', '');
const capitalize = (t) => t.charAt(0).toUpperCase() + t.slice(1);

// Axis (short) and tooltip (long) labels: "25 oct" / "Semana del 20 oct" /
// "Oct" · "Octubre 2026".
function formatPeriodKey(key, period, long) {
  const d = parseKey(key);
  if (period === 'monthly') {
    const m = new Intl.DateTimeFormat('es-ES', { month: long ? 'long' : 'short' }).format(d).replace('.', '');
    return long ? `${capitalize(m)} ${d.getFullYear()}` : capitalize(m);
  }
  if (period === 'weekly') return long ? `Semana del ${shortDay(d)}` : shortDay(d);
  return shortDay(d);
}

// `tone` is a theme token (success / danger / info / brand) — the tile's icon
// color and its tinted background follow the current theme.
function KpiTile({ label, value, sub, delta, goodWhenUp = true, icon: Icon, tone }) {
  const hasDelta = delta !== null && delta !== undefined && isFinite(delta);
  const up = hasDelta && delta > 0;
  const good = hasDelta && (up === goodWhenUp);
  return (
    <div className="glass-card rounded-2xl p-4 sm:p-5 relative overflow-hidden noise-overlay">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `rgb(var(--${tone}-rgb) / 0.12)` }}>
          <Icon className="w-4 h-4" style={{ color: `rgb(var(--${tone}-rgb))` }} />
        </div>
        <span className="text-slate-400 text-xs font-medium">{label}</span>
      </div>
      <p className="text-xl sm:text-2xl font-bold text-white metric-value truncate">{value}</p>
      <div className="flex items-center gap-2 mt-1 min-h-[18px]">
        {hasDelta ? (
          <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${good ? 'text-emerald-400' : 'text-rose-400'}`}>
            {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {Math.abs(delta).toFixed(0)}%
          </span>
        ) : null}
        {sub && <span className="text-xs text-slate-500">{sub}</span>}
      </div>
    </div>
  );
}

// Stand-in for KPIs + charts while a fetch is in flight — never show the
// previous range's numbers (even dimmed) as if they were the current ones.
function ChartsSkeleton() {
  return (
    <div className="space-y-6" data-testid="charts-loading">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="glass-card rounded-2xl p-4 sm:p-5 space-y-3">
            <Skeleton className="h-4 w-20 bg-white/[0.06]" />
            <Skeleton className="h-7 w-28 bg-white/[0.06]" />
          </div>
        ))}
      </div>
      <Skeleton className="h-[328px] w-full rounded-2xl bg-white/[0.04]" />
      <Skeleton className="h-[288px] w-full rounded-2xl bg-white/[0.04]" />
    </div>
  );
}

export default function Charts({ rangeReady = true, dateRange, setDateRange, budgetPeriod, budgetCategories = [] }) {
  const { hidden } = useAmountsVisibility();
  const ct = useChartTheme();
  const [period, setPeriod] = useState('daily');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSeq = useRef(0);
  const fetchData = useCallback(async () => {
    // Wait for App to default the range — fetching before that would load
    // the backend's own default window first, then the real one.
    if (!rangeReady) return;
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const params = { period };
      if (dateRange?.from) params.start_date = dateRange.from.toISOString();
      if (dateRange?.to) {
        const toDate = new Date(dateRange.to);
        toDate.setHours(23, 59, 59, 999);
        params.end_date = toDate.toISOString();
      }
      const data = await getCharts(params);
      if (seq !== fetchSeq.current) return;  // a newer request superseded this one
      setChartData(data);
      setLoading(false);
    } catch (e) {
      console.error('Error fetching charts:', e);
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [period, dateRange, rangeReady]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatXLabel = (val) => formatPeriodKey(val, period, false);
  const formatTooltipLabel = (val) => formatPeriodKey(val, period, true);

  // The header (title, date picker, period tabs) stays put while loading;
  // only the numbers below are swapped for a skeleton, so the user never
  // sees a stale or partial amount — not even dimmed.
  const showSkeleton = loading || !chartData;
  const { timeline = [], categories = [], summary = {}, previous = null } = chartData || {};
  const expenseCategories = categories.filter(c => c.expense > 0).sort((a, b) => b.expense - a.expense);
  const totalExpense = summary.expense || expenseCategories.reduce((s, c) => s + c.expense, 0);
  const savingsRate = Math.round((summary.savings_rate || 0) * 100);
  const prevSavingsRate = previous ? Math.round((previous.savings_rate || 0) * 100) : null;
  const hasData = timeline.length > 0 || expenseCategories.length > 0;
  const maxExpense = expenseCategories[0]?.expense || 0;

  // Budgets only mean something against one whole budget period (a month for
  // monthly budgets…) — with "3 meses" selected, bars just show amounts.
  const bp = budgetPeriod || 'monthly';
  const budgetMode = !!dateRange?.from && dateRangeKey(periodRange(bp, dateRange.from)) === dateRangeKey(dateRange);
  const budgets = new Map(budgetCategories
    .filter(c => c.budget > 0 && c.type !== 'income')
    .map(c => [c.name.trim().toLowerCase(), c.budget]));
  const hasAnyBudget = budgets.size > 0;
  const categoryRows = expenseCategories.map(c => {
    const budget = budgetMode ? (budgets.get(c.name.trim().toLowerCase()) || 0) : 0;
    return { c, budget, over: budget ? c.expense - budget : 0 };
  });
  const overCount = categoryRows.filter(r => r.over > 0).length;

  // Running totals for "Lo que entró vs lo que salió".
  const cumulative = [];
  timeline.forEach(t => {
    const prev = cumulative[cumulative.length - 1] || { income: 0, expense: 0 };
    cumulative.push({ date: t.date, income: prev.income + t.income, expense: prev.expense + t.expense });
  });

  // One-line takeaway above the charts, computed from the numbers shown.
  const now = new Date();
  const isCurrentRange = !!dateRange?.from && dateRange.from <= now && (!dateRange.to || dateRange.to >= now);
  const periodWord = !budgetMode ? 'en este periodo'
    : isCurrentRange ? ({ weekly: 'esta semana', daily: 'hoy' }[bp] || 'este mes')
    : bp === 'monthly' ? `en ${new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(dateRange.from)}`
    : bp === 'weekly' ? 'esa semana' : 'ese día';
  const income = summary.income || 0;
  const expense = summary.expense || 0;
  const headline = overCount > 0
    ? `Te pasaste en ${overCount} ${overCount === 1 ? 'categoría' : 'categorías'} ${periodWord}`
    : income > 0
      ? `Gastaste el ${Math.round((expense / income) * 100)}% de lo que entró ${periodWord}`
      : expense > 0 ? `Gastaste ${formatAmount(expense, hidden)} ${periodWord}, sin ingresos anotados` : null;
  const topCat = expenseCategories[0];
  const subline = [
    budgetMode && hasAnyBudget && overCount === 0 ? 'Vas dentro de todos tus presupuestos.' : null,
    topCat ? `Tu mayor gasto fue ${topCat.name} (${formatAmount(topCat.expense, hidden)}).` : null,
  ].filter(Boolean).join(' ');

  return (
    <div className="space-y-6" data-testid="charts-page">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="w-full lg:w-auto">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Análisis</h1>
            <ProfileButton className="-mr-1.5" />
          </div>
          <p className="text-slate-400 text-sm mt-1">Tus finanzas en el periodo seleccionado</p>
        </div>
        {/* Always one row — no wrap. On narrow phones the date picker is
            the one that gives (min-w-0 + its label truncates) while the
            period tabs keep their size. */}
        <div className="flex flex-row flex-nowrap items-center gap-2 sm:gap-3 w-full lg:w-auto">
          <DateRangePicker className="min-w-0" dateRange={dateRange} setDateRange={setDateRange} budgetPeriod={budgetPeriod} />
          <Tabs value={period} onValueChange={setPeriod} className="shrink-0">
            <TabsList className="bg-slate-800/70 rounded-xl" data-testid="period-tabs">
              <TabsTrigger value="daily" className="rounded-lg text-xs px-2 sm:px-3 data-[state=active]:bg-slate-700 data-[state=active]:text-white" data-testid="tab-daily">Diario</TabsTrigger>
              <TabsTrigger value="weekly" className="rounded-lg text-xs px-2 sm:px-3 data-[state=active]:bg-slate-700 data-[state=active]:text-white" data-testid="tab-weekly">Semanal</TabsTrigger>
              <TabsTrigger value="monthly" className="rounded-lg text-xs px-2 sm:px-3 data-[state=active]:bg-slate-700 data-[state=active]:text-white" data-testid="tab-monthly">Mensual</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {showSkeleton ? (
        <ChartsSkeleton />
      ) : !hasData ? (
        <div className="text-center py-16">
          <p className="text-slate-500">No hay datos suficientes para este periodo. Prueba otro rango o agrega transacciones.</p>
        </div>
      ) : (
        <>
          {headline && (
            <div className="glass-card rounded-2xl p-5 flex items-start gap-3" data-testid="charts-conclusion">
              {overCount > 0 && <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-rose-400" />}
              <div className="min-w-0">
                <p className="text-lg font-semibold text-white leading-snug">{headline}</p>
                {subline && <p className="text-sm text-slate-400 mt-1">{subline}</p>}
              </div>
            </div>
          )}

          {/* KPI tiles with period-over-period comparison */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="kpi-tiles">
            <KpiTile label="Ingresos" value={formatAmount(summary.income || 0, hidden)} icon={TrendingUp} tone="success"
              delta={pctChange(summary.income, previous?.income)} goodWhenUp />
            <KpiTile label="Gastos" value={formatAmount(summary.expense || 0, hidden)} icon={TrendingDown} tone="danger"
              delta={pctChange(summary.expense, previous?.expense)} goodWhenUp={false} />
            {/* No % here: against a near-zero previous net it reads "↗ 1256%". */}
            <KpiTile label="Balance neto" value={formatAmount(summary.net || 0, hidden)} icon={Wallet} tone="info" />
            <KpiTile label="Tasa de ahorro" value={`${savingsRate}%`} icon={PiggyBank} tone="brand"
              delta={prevSavingsRate !== null ? savingsRate - prevSavingsRate : null} goodWhenUp
              sub={previous ? 'vs periodo anterior' : undefined} />
          </div>

          <div className="space-y-6">
            {/* Balance over time */}
            {timeline.length > 0 && (
              <div className="glass-card rounded-2xl p-6 noise-overlay relative overflow-hidden" data-testid="balance-chart">
                <h2 className="text-base font-semibold text-white mb-4">Balance en el tiempo</h2>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={timeline} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                    {ct.areaGradient('balanceGrad')}
                    <CartesianGrid {...ct.gridProps} />
                    <XAxis dataKey="date" tickFormatter={formatXLabel} {...ct.axisProps} />
                    <YAxis tickFormatter={v => `$${v}`} width={48} {...ct.axisProps} />
                    <Tooltip content={<ChartTooltip formatLabel={formatTooltipLabel} formatValue={(v) => formatAmount(v, hidden)} />} />
                    <Area
                      type="monotone"
                      dataKey="balance"
                      name="Balance"
                      fill="url(#balanceGrad)"
                      dot={ct.lastPointDot(timeline.length)}
                      isAnimationActive={false}
                      {...ct.lineProps}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Gastos por categoría (redesign.md, Fase 7): ranked bars only — no
                donut, one brand color (category color only on the icon). When
                the range is one whole budget period, each bar is measured
                against its budget, with a tick at the limit and the state in
                words. */}
            {expenseCategories.length > 0 && (
              <div className="glass-card rounded-2xl p-5 sm:p-6" data-testid="category-breakdown">
                <div className="flex items-baseline justify-between gap-3 mb-4">
                  <h2 className="text-base font-semibold text-white">Gastos por categoría</h2>
                  {budgetMode && hasAnyBudget && <span className="text-xs text-slate-500">vs. tu presupuesto</span>}
                </div>
                <div className="space-y-4" data-testid="category-drilldown">
                  {categoryRows.map(({ c, budget, over }) => {
                    const Icon = getIcon(c.icon);
                    const scale = budget ? Math.max(c.expense, budget) : maxExpense;
                    const share = totalExpense > 0 ? (c.expense / totalExpense) * 100 : 0;
                    return (
                      <div key={c.name} data-testid={`drilldown-${c.name}`}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: catTint(c.color) }}>
                              <Icon className="w-3.5 h-3.5" style={{ color: catColor(c.color) }} />
                            </span>
                            <span className="text-sm font-medium text-slate-200 truncate">{c.name}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-sm font-semibold text-slate-200 metric-value">{formatAmount(c.expense, hidden)}</span>
                            {!budget && <span className="text-xs text-slate-500 tabular-nums w-9 text-right">{share.toFixed(0)}%</span>}
                          </div>
                        </div>
                        {/* Within budget: the full track is the budget, filled
                            in brand. Over it: brand up to the limit, then the
                            excess in red — no separate limit marker. */}
                        <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-800" data-testid={`bar-${c.name}`}>
                          <div
                            className="h-full bg-brand transition-[width] duration-500"
                            style={{ width: `${scale > 0 ? (Math.min(c.expense, budget || c.expense) / scale) * 100 : 0}%` }}
                          />
                          {over > 0 && (
                            <div className="h-full bg-rose-500 transition-[width] duration-500" style={{ width: `${(over / scale) * 100}%` }} />
                          )}
                        </div>
                        {budget > 0 && (
                          <p className={`mt-1 text-xs ${over > 0 ? 'text-rose-400 font-medium' : 'text-slate-500'}`}>
                            {over > 0
                              ? `Te pasaste ${formatAmount(over, hidden)} de ${formatAmount(budget, hidden)}`
                              : `Quedan ${formatAmount(-over, hidden)} de ${formatAmount(budget, hidden)}`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lo que entró vs lo que salió (Fase 7): running totals, so a
                payday shows as one step instead of a bar that flattens every
                other day; the gap between the lines is what's left, and the
                expense line crossing above means spending more than came in. */}
            {cumulative.length > 0 && (
              <div className="glass-card rounded-2xl p-5 sm:p-6" data-testid="income-expense-chart">
                <h2 className="text-base font-semibold text-white">Lo que entró vs lo que salió</h2>
                <p className="text-xs text-slate-500 mt-0.5 mb-4">Acumulado del periodo · la distancia entre las líneas es lo que te queda</p>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={cumulative} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                    <CartesianGrid {...ct.gridProps} />
                    <XAxis dataKey="date" tickFormatter={formatXLabel} {...ct.axisProps} />
                    <YAxis tickFormatter={v => `$${v}`} width={48} {...ct.axisProps} />
                    <Tooltip content={<ChartTooltip formatLabel={formatTooltipLabel} formatValue={(v) => formatAmount(v, hidden)} />} />
                    <Legend wrapperStyle={{ paddingTop: 12 }} formatter={(value) => <span className="text-xs text-slate-300">{value}</span>} />
                    <Line type="stepAfter" dataKey="income" name="Entró" stroke={ct.income} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                    <Line type="monotone" dataKey="expense" name="Salió" stroke={ct.hue} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
